import React, { useEffect, useMemo, useState } from 'react';
import LgmButton from './LgmButton';
import FdaSourceResultRow from './FdaSourceResultRow';
import { FDA_SOURCE_FILTER_LABELS as SOURCE_LABELS } from './FdaSourceFilterControls';
import type { FdaSourceFilter as SourceFilter } from './FdaSourceFilterControls';
import { getFdaSourceRowId } from '../domains/products/adapters/fdaSourceSearchFieldConfig';
import { useFdaSourceSearch } from '../services/api/domains/products/hooks';
import {
  FdaSourceKey,
  FdaSourceObRow,
  FdaSourceNdcRow,
  FdaSourceDmfRow,
} from '../services/api/domains/products/types';
import { GRAY_200, GRAY_500, GRAY_900 } from 'theme/grayScale';

interface FdaSourceSearchResultsProps {
  searchTerm: string;
  /** Owned by the parent toolbar (FdaSourceFilterControls) so both the
   * toolbar and these result columns share one source of truth. */
  sourceFilter: SourceFilter;
  pageSize: number;
  /** 'unlinked'/'linked' (docs/features/fda-source-record-browser.md, Task
   * FSU-004; 'linked' added for the "Sourceable via Navigator" deep link,
   * human direction, 2026-09-28) — an (anti-)join browse mode, independent
   * of searchTerm. Defaults to 'all', the pre-FSU-004 behavior. */
  linkFilter?: 'all' | 'unlinked' | 'linked';
  /** Wire-shaped filter values (matching api/routes/fdaSourceSearch.js's
   * own query params), only meaningful under linkFilter='unlinked'/'linked'
   * and only for the source they name -- see useFdaSourceSearch's own doc
   * comment. */
  obType?: string[];
  ndcFinished?: string;
  dmfStatus?: string;
}

/**
 * FDA Sources search results — three independent, side-by-side per-source
 * lists ("All" mode) or one full-width list (a specific source filter),
 * each backed by its own `useFdaSourceSearch` call so loading and
 * pagination are genuinely independent per column (docs/features/
 * fda-source-product-search.md, Task FSS-008, Architecture Decision 10/12).
 */
const FdaSourceSearchResults = ({
  searchTerm,
  sourceFilter,
  pageSize,
  linkFilter = 'all',
  obType,
  ndcFinished,
  dmfStatus,
}: FdaSourceSearchResultsProps) => {
  const [obPage, setObPage] = useState(1);
  const [ndcPage, setNdcPage] = useState(1);
  const [dmfPage, setDmfPage] = useState(1);

  // A stale page number from a previous search term, filter, or page size can be out
  // of range for the new result set (backend OFFSET/FETCH past the end
  // returns results: [] with a non-zero total), producing a false "no
  // results" state. Reset every column back to page 1 whenever the term
  // or filter changes, same as the existing page-size reset above.
  useEffect(() => {
    setObPage(1);
    setNdcPage(1);
    setDmfPage(1);
  }, [
    searchTerm,
    sourceFilter,
    pageSize,
    linkFilter,
    obType,
    ndcFinished,
    dmfStatus,
  ]);

  const showOb = sourceFilter === 'all' || sourceFilter === 'ob';
  const showNdc = sourceFilter === 'all' || sourceFilter === 'ndc';
  const showDmf = sourceFilter === 'all' || sourceFilter === 'dmf';

  // Memoized so each params object's reference is stable across renders
  // where its own values didn't change -- useFdaSourceSearch/useQuery key
  // their fetch effect on this reference, and a fresh object literal every
  // render would re-trigger the fetch on every render (an infinite loop:
  // fetch -> state update -> re-render -> new object -> fetch again).
  const obParams = useMemo(
    () =>
      showOb
        ? {
            search: searchTerm,
            source: 'ob' as const,
            page: obPage,
            pageSize,
            linkFilter,
            obType,
          }
        : undefined,
    [showOb, searchTerm, obPage, pageSize, linkFilter, obType]
  );
  const ndcParams = useMemo(
    () =>
      showNdc
        ? {
            search: searchTerm,
            source: 'ndc' as const,
            page: ndcPage,
            pageSize,
            linkFilter,
            ndcFinished,
          }
        : undefined,
    [showNdc, searchTerm, ndcPage, pageSize, linkFilter, ndcFinished]
  );
  const dmfParams = useMemo(
    () =>
      showDmf
        ? {
            search: searchTerm,
            source: 'dmf' as const,
            page: dmfPage,
            pageSize,
            linkFilter,
            dmfStatus,
          }
        : undefined,
    [showDmf, searchTerm, dmfPage, pageSize, linkFilter, dmfStatus]
  );

  const obResult = useFdaSourceSearch(obParams);
  const ndcResult = useFdaSourceSearch(ndcParams);
  const dmfResult = useFdaSourceSearch(dmfParams);

  // An empty term is a real, intentional query under linkFilter=unlinked/
  // linked (docs/features/fda-source-record-browser.md, Task FSU-004) --
  // "the whole linked/unlinked cohort," not "search not yet started." Only
  // show the placeholder in normal (linkFilter='all') mode.
  const hasQuery =
    searchTerm.trim() !== '' ||
    linkFilter === 'unlinked' ||
    linkFilter === 'linked';

  return (
    <div style={styles.container} data-testid="fda-source-search-results">
      {!hasQuery && (
        <div style={styles.state}>
          Type a search term above to search FDA Sources.
        </div>
      )}

      {hasQuery && (
        <div
          style={sourceFilter === 'all' ? styles.columns : styles.singleColumn}>
          {showOb && (
            <FdaSourceResultColumn
              source="ob"
              label={SOURCE_LABELS.ob}
              fullWidth={sourceFilter !== 'all'}
              state={obResult}
              page={obPage}
              onPageChange={setObPage}
              pageSize={pageSize}
            />
          )}
          {showNdc && (
            <FdaSourceResultColumn
              source="ndc"
              label={SOURCE_LABELS.ndc}
              fullWidth={sourceFilter !== 'all'}
              state={ndcResult}
              page={ndcPage}
              onPageChange={setNdcPage}
              pageSize={pageSize}
            />
          )}
          {showDmf && (
            <FdaSourceResultColumn
              source="dmf"
              label={SOURCE_LABELS.dmf}
              fullWidth={sourceFilter !== 'all'}
              state={dmfResult}
              page={dmfPage}
              onPageChange={setDmfPage}
              pageSize={pageSize}
            />
          )}
        </div>
      )}
    </div>
  );
};

type ColumnRow = FdaSourceObRow | FdaSourceNdcRow | FdaSourceDmfRow;

interface ColumnApiState {
  status: 'idle' | 'loading' | 'success' | 'error';
  data?: {
    ob?: {
      results: FdaSourceObRow[];
      page: number;
      pageSize: number;
      total: number;
    };
    ndc?: {
      results: FdaSourceNdcRow[];
      page: number;
      pageSize: number;
      total: number;
    };
    dmf?: {
      results: FdaSourceDmfRow[];
      page: number;
      pageSize: number;
      total: number;
    };
  };
  error?: { message: string };
}

interface FdaSourceResultColumnProps {
  source: FdaSourceKey;
  label: string;
  fullWidth: boolean;
  state: ColumnApiState;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
}

/** One column — independent loading/empty/error state and pagination per Decision 10/12. */
const FdaSourceResultColumn = ({
  source,
  label,
  fullWidth,
  state,
  page,
  pageSize,
  onPageChange,
}: FdaSourceResultColumnProps) => {
  const sourceResult = state.data?.[source];
  const results: ColumnRow[] = sourceResult?.results ?? [];
  const total = sourceResult?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div
      style={fullWidth ? styles.columnFullWidth : styles.column}
      data-testid={`fda-source-column-${source}`}>
      <div style={styles.columnHeader}>
        <span style={styles.columnTitle}>{label}</span>
        {state.status === 'success' && (
          <span style={styles.columnCount}>
            {total} result{total === 1 ? '' : 's'}
          </span>
        )}
      </div>

      {state.status === 'loading' && (
        <div style={styles.state} data-testid={`fda-source-loading-${source}`}>
          Loading…
        </div>
      )}

      {state.status === 'error' && (
        <div
          style={styles.stateError}
          data-testid={`fda-source-error-${source}`}>
          Couldn’t load {label} results:{' '}
          {state.error?.message ?? 'unknown error'}
        </div>
      )}

      {state.status === 'success' && results.length === 0 && (
        <div style={styles.state}>No {label} results.</div>
      )}

      {state.status === 'success' && results.length > 0 && (
        <>
          <ul style={styles.list} aria-label={`${label} results`}>
            {results.map(row => (
              <FdaSourceResultRow
                key={getFdaSourceRowId(source, row)}
                source={source}
                row={row}
              />
            ))}
          </ul>
          {totalPages > 1 && (
            <div style={styles.pagination}>
              <LgmButton
                type="button"
                variant="outlined"
                disabled={page <= 1}
                onClick={() => onPageChange(page - 1)}
                aria-label={`Previous page of ${label} results`}
                style={styles.pageButton}>
                Prev
              </LgmButton>
              <span style={styles.pageIndicator}>
                Page {page} of {totalPages}
              </span>
              <LgmButton
                type="button"
                variant="outlined"
                disabled={page >= totalPages}
                onClick={() => onPageChange(page + 1)}
                aria-label={`Next page of ${label} results`}
                style={styles.pageButton}>
                Next
              </LgmButton>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
    padding: '0.75rem',
    height: '100%',
    minHeight: 0,
    overflow: 'hidden',
  },
  columns: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.75rem',
    alignItems: 'stretch',
    flex: '1 1 auto',
    minHeight: 0,
    overflow: 'hidden',
  },
  singleColumn: {
    display: 'flex',
    flex: '1 1 auto',
    minHeight: 0,
    overflow: 'hidden',
  },
  column: {
    flex: '1 1 18rem',
    minWidth: '18rem',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    border: `1px solid ${GRAY_200}`,
    borderRadius: '4px',
    padding: '0.5rem',
    overflow: 'hidden',
  },
  columnFullWidth: {
    flex: '1 1 auto',
    width: '100%',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    border: `1px solid ${GRAY_200}`,
    borderRadius: '4px',
    padding: '0.5rem',
    overflow: 'hidden',
  },
  columnHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    flexShrink: 0,
  },
  columnTitle: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: GRAY_900,
  },
  columnCount: {
    fontSize: '0.75rem',
    color: GRAY_500,
  },
  list: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '0.375rem',
    flex: '1 1 auto',
    minHeight: 0,
    overflowY: 'auto',
  },
  state: {
    padding: '1rem',
    fontSize: '0.8125rem',
    color: GRAY_500,
    textAlign: 'center',
  },
  stateError: {
    padding: '1rem',
    fontSize: '0.8125rem',
    color: '#b91c1c',
    textAlign: 'center',
  },
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '0.5rem',
    flexShrink: 0,
  },
  pageButton: {
    fontSize: '0.75rem',
    padding: '0.25rem 0.625rem',
  },
  pageIndicator: {
    fontSize: '0.75rem',
    color: GRAY_500,
  },
};

export default FdaSourceSearchResults;
